/**
 * Seção "Gerar rascunho com IA" do dialog de nova proposta (PR-M1-01c-i).
 *
 * Componente extraído do `NewProposalDialog` para manter o pai abaixo
 * do hard limit de 300 linhas. Dois textareas dedicados:
 *
 *   1. **Contexto sobre o lead** — fatos / background. A IA usa como
 *      informação de entrada.
 *   2. **O que destacar** — instruções imperativas. A IA prioriza
 *      refletir esses pontos no `notes_pt_br`.
 *
 * Checkbox "Incluir respostas do questionário" — default **on**.
 *
 * Estado visual:
 *   - Colapsado por default (expande ao clicar em "Gerar rascunho com IA").
 *   - Enquanto gera: botão "Gerando…" + aria-busy.
 *   - Ao sucesso: pai popula os campos de revisão; consumer decide se
 *     fecha/colapsa este bloco.
 *   - Erro: mensagem textual abaixo do botão — não fecha nada, Karla
 *     pode ajustar e tentar de novo ou preencher manual.
 */
import { useState } from 'react';
import { Button, Label } from '@kmgestao/ui';

export interface NewProposalAiAssistantProps {
  /** Estado "gerando" — bloqueia UI e muda label do botão. */
  isGenerating: boolean;
  /** Mensagem de erro do último "gerar" — null esconde o aviso. */
  errorMessage: string | null;
  /** Valor atual do campo de contexto (controlled). Persiste via
   * `ai_context` no form do pai, que salva no banco em create/update
   * (PR-M1-01c-ii.m, 2026-04-24 — Alexandre pediu reaproveitar). */
  context: string;
  onContextChange: (next: string) => void;
  /** Valor atual do campo de destaques (controlled). */
  highlights: string;
  onHighlightsChange: (next: string) => void;
  /** Disparado ao clicar em "Gerar rascunho". Pai decide o que fazer
   * com o resultado (chamar edge function, popular form, etc). */
  onGenerate: (input: {
    context: string;
    highlights: string;
    includeQuestionnaireAnswers: boolean;
  }) => void;
}

export function NewProposalAiAssistant({
  isGenerating,
  errorMessage,
  context,
  onContextChange,
  highlights,
  onHighlightsChange,
  onGenerate,
}: NewProposalAiAssistantProps) {
  const [includeQuestionnaireAnswers, setIncludeQuestionnaireAnswers] = useState(true);

  const handleGenerate = () => {
    onGenerate({ context, highlights, includeQuestionnaireAnswers });
  };

  return (
    <details className="group rounded-lg border border-border bg-muted/30 open:bg-muted/40">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 p-4 text-sm font-medium text-foreground hover:bg-muted/50">
        <span className="flex items-center gap-2">
          <span aria-hidden="true">✨</span>
          Gerar rascunho com IA
        </span>
        <span
          aria-hidden="true"
          className="text-xs text-muted-foreground transition-transform group-open:rotate-180"
        >
          ▾
        </span>
      </summary>

      <div className="space-y-4 border-t border-border p-4">
        <div className="space-y-1.5">
          <Label htmlFor="ai-context">Contexto sobre o lead</Label>
          <textarea
            id="ai-context"
            rows={4}
            value={context}
            onChange={(e) => onContextChange(e.target.value)}
            maxLength={4_000}
            disabled={isGenerating}
            placeholder="Ortodontia SP, 3 cadeiras, equipe de 2 médicos. Interessado no Pro mas apertado de orçamento."
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Background do lead — informações que a IA deve considerar.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ai-highlights">
            O que destacar nesta proposta <span className="text-muted-foreground">(opcional)</span>
          </Label>
          <textarea
            id="ai-highlights"
            rows={3}
            value={highlights}
            onChange={(e) => onHighlightsChange(e.target.value)}
            maxLength={2_000}
            disabled={isGenerating}
            placeholder="Enfatizar redução de no-show com lembretes automáticos. Oferecer trial de 14 dias."
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Instruções que a IA deve refletir nas notas da proposta — prioridade alta.
          </p>
        </div>

        <div className="flex items-start gap-2">
          <input
            id="ai-include-questionnaire"
            type="checkbox"
            checked={includeQuestionnaireAnswers}
            onChange={(e) => setIncludeQuestionnaireAnswers(e.target.checked)}
            disabled={isGenerating}
            className="mt-0.5 size-4 cursor-pointer accent-primary disabled:cursor-not-allowed"
          />
          <Label
            htmlFor="ai-include-questionnaire"
            className="cursor-pointer text-sm font-normal text-foreground"
          >
            Incluir respostas do questionário como contexto
          </Label>
        </div>

        {errorMessage ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
          >
            {errorMessage}
          </p>
        ) : null}

        <div className="flex justify-end">
          <Button
            type="button"
            size="lg"
            onClick={handleGenerate}
            disabled={isGenerating}
            aria-busy={isGenerating || undefined}
          >
            {isGenerating ? 'Gerando…' : '✨ Gerar rascunho'}
          </Button>
        </div>
      </div>
    </details>
  );
}
